<template>
  <div class="rdp-wrap" ref="wrap">

    <!-- ── Trigger ───────────────────────────────────────────────────── -->
    <button class="rdp-trigger" :class="{ 'rdp-trigger--open': isOpen }" @click="toggle">
      <i class="material-icons">event</i>
      <span class="rdp-trigger-text">{{ triggerLabel }}</span>
      <span v-if="compareMode !== 'none'" class="rdp-badge">{{ compareBadge }}</span>
      <i class="material-icons rdp-caret">expand_more</i>
    </button>

    <!-- ── Popup ─────────────────────────────────────────────────────── -->
    <transition name="rdp-fade">
      <div class="rdp-popup" v-if="isOpen" @mousedown.stop>
        <div class="rdp-layout">

          <!-- Calendrier -->
          <div class="rdp-cal">
            <div class="rdp-cal-header">
              <Tooltip text="Année précédente" position="bottom" classCustom="rdp-tt">
                <button class="rdp-nav rdp-nav--year" @click.stop="prevYear">
                  <i class="material-icons">first_page</i>
                </button>
              </Tooltip>
              <Tooltip text="Mois précédent" position="bottom" classCustom="rdp-tt">
                <button class="rdp-nav rdp-nav--month" @click.stop="prevMonth">
                  <i class="material-icons">chevron_left</i>
                </button>
              </Tooltip>
              <span class="rdp-cal-title">{{ monthLabel }}</span>
              <Tooltip text="Mois suivant" position="bottom" classCustom="rdp-tt">
                <button class="rdp-nav rdp-nav--month" @click.stop="nextMonth">
                  <i class="material-icons">chevron_right</i>
                </button>
              </Tooltip>
              <Tooltip text="Année suivante" position="bottom" classCustom="rdp-tt">
                <button class="rdp-nav rdp-nav--year" @click.stop="nextYear">
                  <i class="material-icons">last_page</i>
                </button>
              </Tooltip>
            </div>

            <div class="rdp-weekdays">
              <span v-for="d in dayNames" :key="d">{{ d }}</span>
            </div>

            <div class="rdp-grid" @mouseleave="hoverDate = null">
              <div
                v-for="(cell, i) in calendarCells"
                :key="i"
                class="rdp-cell"
                :class="cellClass(cell)"
                @click.stop="onDayClick(cell)"
                @mouseenter="onDayHover(cell)"
              >
                <span class="rdp-cell-inner">{{ cell.day }}</span>
              </div>
            </div>

            <p class="rdp-selecting-hint" v-if="selectingEnd">
              Cliquez pour définir la date de fin
            </p>
          </div>

          <!-- Panneau droit -->
          <div class="rdp-panel">

            <!-- Périodes rapides -->
            <p class="rdp-panel-title">Période rapide</p>
            <ul class="rdp-presets">
              <li
                v-for="p in presets"
                :key="p.key"
                class="rdp-preset"
                :class="{ 'rdp-preset--active': activePreset === p.key }"
                @click.stop="applyPreset(p)"
              >
                <i class="material-icons rdp-preset-check">check</i>
                {{ p.label }}
              </li>
            </ul>

            <div class="rdp-sep"></div>

            <!-- Comparaison -->
            <label class="rdp-compare-header" @click.stop>
              <span class="rdp-check-box" :class="{ 'rdp-check-box--on': compareEnabled }" @click.stop="compareEnabled = !compareEnabled">
                <i class="material-icons">{{ compareEnabled ? 'check_box' : 'check_box_outline_blank' }}</i>
              </span>
              <span class="rdp-compare-label">Comparer à</span>
            </label>

            <ul class="rdp-compare-opts" v-if="compareEnabled">
              <li
                v-for="opt in compareOptions"
                :key="opt.key"
                class="rdp-compare-opt"
                :class="{ 'rdp-compare-opt--active': tempCompareMode === opt.key, 'rdp-compare-opt--off': !opt.available }"
                @click.stop="opt.available && (tempCompareMode = opt.key)"
              >
                <span class="rdp-radio" :class="{ 'rdp-radio--on': tempCompareMode === opt.key }"></span>
                {{ opt.label }}
                <i class="material-icons rdp-opt-check" v-if="tempCompareMode === opt.key">check</i>
              </li>
            </ul>

          </div>
        </div>

        <!-- Footer -->
        <div class="rdp-footer">
          <span class="rdp-footer-range">
            <i class="material-icons">calendar_today</i>
            {{ formatDate(tempStart) }} &nbsp;→&nbsp; {{ formatDate(tempEnd) }}
            <span class="rdp-footer-days">({{ periodDays }} j.)</span>
          </span>
          <button class="rdp-btn-apply" @click.stop="apply">Appliquer</button>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import moment from 'moment'
import Tooltip from '../tooltip/tooltipInit.vue'

const PRESETS = [
  { key: '1w',  label: '1 semaine',           days: 7 },
  { key: '2w',  label: '2 semaines',           days: 14 },
  { key: 'mtd', label: 'Depuis début du mois', mtd: true },
  { key: '1m',  label: 'Mois précédent',       prevMonth: true },
  { key: '3m',  label: '3 mois',               days: 90 },
  { key: '6m',  label: '6 mois',               days: 180 },
  { key: '1y',  label: '1 an',                 days: 365 },
]

export default {
  name: 'ReportingDatePicker',
  components: { Tooltip },

  props: {
    dateStart:   { type: String, required: true },
    dateEnd:     { type: String, required: true },
    compareMode: { type: String, default: 'previous' },
  },

  data() {
    return {
      isOpen:          false,
      tempStart:       this.dateStart,
      tempEnd:         this.dateEnd,
      selectingEnd:    false,
      hoverDate:       null,
      calYear:         moment(this.dateEnd).year(),
      calMonth:        moment(this.dateEnd).month(),
      tempCompareMode: this.compareMode === 'none' ? 'previous' : this.compareMode,
      compareEnabled:  this.compareMode !== 'none',
      activePreset:    null,
      presets:         PRESETS,
    }
  },

  computed: {
    locale() {
      return (this.$i18n && this.$i18n.locale) || 'fr'
    },

    dayNames() {
      var loc = this.locale
      return [1, 2, 3, 4, 5, 6, 7].map(function(d) {
        return moment().isoWeekday(d).locale(loc).format('dd')
      })
    },

    monthLabel() {
      return moment({ year: this.calYear, month: this.calMonth }).locale(this.locale).format('MMMM YYYY')
    },

    calendarCells() {
      var year  = this.calYear
      var month = this.calMonth
      var first = moment({ year, month, day: 1 })
      var last  = moment({ year, month }).endOf('month')
      var today = moment().format('YYYY-MM-DD')
      var cells = []

      var startDow = (first.day() + 6) % 7
      for (var p = 0; p < startDow; p++) {
        var d = first.clone().subtract(startDow - p, 'days')
        cells.push({ date: d.format('YYYY-MM-DD'), day: d.date(), out: true, disabled: true })
      }
      for (var i = 1; i <= last.date(); i++) {
        var cur = moment({ year, month, day: i })
        cells.push({ date: cur.format('YYYY-MM-DD'), day: i, out: false, disabled: cur.format('YYYY-MM-DD') > today })
      }
      var endDow = (last.day() + 6) % 7
      for (var j = 1; j <= (endDow < 6 ? 6 - endDow : 0); j++) {
        var d2 = last.clone().add(j, 'days')
        cells.push({ date: d2.format('YYYY-MM-DD'), day: d2.date(), out: true, disabled: true })
      }
      return cells
    },

    periodDays() {
      return moment(this.tempEnd).diff(moment(this.tempStart), 'days') + 1
    },

    compareOptions() {
      var days = this.periodDays
      return [
        { key: 'previous', label: 'Période précédente (' + days + ' j)', available: true },
        { key: 'month',    label: 'Même période, mois précédent',   available: days <= 31 },
        { key: 'year',     label: 'Même période, année précédente', available: days <= 365 },
      ]
    },

    triggerLabel() {
      var p = PRESETS.find(function(x) { return x.key === this.activePreset }.bind(this))
      if (p) return p.label
      var loc = this.locale
      var s = moment(this.dateStart).locale(loc)
      var e = moment(this.dateEnd).locale(loc)
      return s.format(s.year() === e.year() ? 'D MMM' : 'D MMM YYYY') + '  →  ' + e.format('D MMM YYYY')
    },

    compareBadge() {
      if (this.compareMode === 'previous') return 'vs préc.'
      if (this.compareMode === 'month')    return 'vs -1 mois'
      if (this.compareMode === 'year')     return 'vs -1 an'
      return ''
    },
  },

  mounted() {
    this._outsideHandler = function(e) {
      if (this.isOpen && this.$refs.wrap && !this.$refs.wrap.contains(e.target)) {
        this.close()
      }
    }.bind(this)
    document.addEventListener('mousedown', this._outsideHandler)
  },

  beforeDestroy() {
    document.removeEventListener('mousedown', this._outsideHandler)
  },

  methods: {
    toggle() { this.isOpen ? this.close() : this.open() },

    open() {
      this.tempStart       = this.dateStart
      this.tempEnd         = this.dateEnd
      this.tempCompareMode = this.compareMode === 'none' ? 'previous' : this.compareMode
      this.compareEnabled  = this.compareMode !== 'none'
      this.selectingEnd    = false
      this.hoverDate       = null
      this.calYear         = moment(this.dateEnd).year()
      this.calMonth        = moment(this.dateEnd).month()
      this.isOpen          = true
    },

    close() {
      this.isOpen       = false
      this.selectingEnd = false
      this.hoverDate    = null
    },

    prevMonth() {
      if (this.calMonth === 0) { this.calYear--; this.calMonth = 11 }
      else this.calMonth--
    },
    nextMonth() {
      if (this.calMonth === 11) { this.calYear++; this.calMonth = 0 }
      else this.calMonth++
    },
    prevYear()  { this.calYear-- },
    nextYear()  {
      var next = moment({ year: this.calYear, month: this.calMonth }).add(1, 'month')
      if (!next.isAfter(moment())) this.calYear++
    },

    onDayClick(cell) {
      if (cell.disabled || cell.out) return
      if (!this.selectingEnd) {
        this.tempStart    = cell.date
        this.tempEnd      = cell.date
        this.selectingEnd = true
        this.activePreset = null
      } else {
        if (cell.date < this.tempStart) {
          this.tempEnd   = this.tempStart
          this.tempStart = cell.date
        } else {
          this.tempEnd = cell.date
        }
        this.selectingEnd = false
        this.hoverDate    = null
        this.activePreset = null
      }
    },

    onDayHover(cell) {
      if (this.selectingEnd && !cell.disabled && !cell.out) this.hoverDate = cell.date
    },

    cellClass(cell) {
      var d    = cell.date
      var s    = this.tempStart
      var hov  = this.selectingEnd && this.hoverDate ? this.hoverDate : null
      var rs   = hov ? (hov < s ? hov : s)   : s
      var re   = hov ? (hov < s ? s   : hov) : this.tempEnd

      return {
        'rdp-cell--out':      cell.out,
        'rdp-cell--disabled': cell.disabled && !cell.out,
        'rdp-cell--today':    !cell.out && d === moment().format('YYYY-MM-DD'),
        'rdp-cell--start':    !cell.out && d === rs,
        'rdp-cell--end':      !cell.out && d === re && re !== rs,
        'rdp-cell--single':   !cell.out && d === rs && rs === re,
        'rdp-cell--in':       !cell.out && d > rs && d < re,
      }
    },

    applyPreset(preset) {
      this.activePreset = preset.key
      if (preset.mtd) {
        this.tempStart = moment().startOf('month').format('YYYY-MM-DD')
        this.tempEnd   = moment().format('YYYY-MM-DD')
      } else if (preset.prevMonth) {
        this.tempStart = moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD')
        this.tempEnd   = moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
      } else {
        this.tempStart = moment().subtract(preset.days, 'days').format('YYYY-MM-DD')
        this.tempEnd   = moment().format('YYYY-MM-DD')
      }
      this.selectingEnd = false
      this.hoverDate    = null
      this.calYear      = moment(this.tempEnd).year()
      this.calMonth     = moment(this.tempEnd).month()
    },

    apply() {
      var mode = this.compareEnabled ? this.tempCompareMode : 'none'
      this.$emit('apply', { dateStart: this.tempStart, dateEnd: this.tempEnd, compareMode: mode, preset: this.activePreset })
      this.close()
    },

    formatDate(d) {
      return d ? moment(d).locale(this.locale).format('D MMM YYYY') : '—'
    },
  },
}
</script>

<style scoped>
/* ── Wrap ────────────────────────────────────────────────────────────── */
.rdp-wrap { position: relative; display: inline-flex; }

/* ── Trigger ─────────────────────────────────────────────────────────── */
.rdp-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent !important;
  border: none;
  border-radius: 10px;
  padding: 7px 10px 7px 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #6B1F4D !important;
  white-space: nowrap;
  transition: background .15s;
  font-family: inherit;
}
.rdp-trigger:hover,
.rdp-trigger--open {
  background: rgba(107,31,77,.07) !important;
}
.rdp-trigger .material-icons:first-child { font-size: 17px; color: #6B1F4D; }
.rdp-caret { font-size: 18px; color: #c8a8c0; transition: transform .2s; }
.rdp-trigger--open .rdp-caret { transform: rotate(180deg); }
.rdp-badge {
  font-size: 10px; font-weight: 700;
  background: #f1edf5; color: #6B1F4D;
  border-radius: 20px; padding: 2px 8px;
}

/* ── Popup ───────────────────────────────────────────────────────────── */
.rdp-popup {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 999;
  background: #fff;
  border-radius: 5.6px !important;
  box-shadow: 0 12px 40px rgba(0,0,0,.13), 0 2px 8px rgba(0,0,0,.06);
  border: 1px solid #ede8f3;
  overflow: hidden;
  min-width: 600px;
}

.rdp-layout {
  display: flex;
}

/* Transition */
.rdp-fade-enter-active, .rdp-fade-leave-active { transition: opacity .15s, transform .15s; }
.rdp-fade-enter, .rdp-fade-leave-to { opacity: 0; transform: translateY(-4px); }

/* ── Calendar ────────────────────────────────────────────────────────── */
.rdp-cal {
  padding: 20px 20px 16px;
  border-right: 1px solid #f1edf5;
  min-width: 288px;
}

.rdp-cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
  margin-bottom: 14px;
}
/* Espace entre bouton année et bouton mois */
.rdp-cal-header .rdp-tt:nth-child(2) { margin-left: 4px; }
.rdp-cal-header .rdp-tt:nth-last-child(2) { margin-right: 4px; }
.rdp-nav {
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  background: none; border: none; border-radius: 7px;
  cursor: pointer;
  transition: background .12s, opacity .12s;
  flex-shrink: 0;
}
.rdp-nav .material-icons { font-size: 17px; }

/* Mois : icône violette, fond au hover */
.rdp-nav--month { background: #f1edf5 !important; color: #121826 !important; }
.rdp-nav--month:hover { color: #6B1F4D !important; }

/* Année : fond violet, icône blanche, espacement externe */
.rdp-nav--year {
  background: #6B1F4D;
  color: #fff;
  border-radius: 7px;
}
.rdp-nav--year:hover { opacity: .82; }
.rdp-cal-title {
  flex: 1; text-align: center;
  font-size: 14px; font-weight: 700; color: #121826;
  text-transform: capitalize;
}

.rdp-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
}
.rdp-weekdays span {
  text-align: center;
  font-size: 11px; font-weight: 700;
  color: #b0a0bc; padding: 4px 0;
  text-transform: uppercase;
}

.rdp-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.rdp-cell {
  display: flex; align-items: center; justify-content: center;
  height: 36px;
  cursor: pointer;
  position: relative;
  user-select: none;
}
.rdp-cell-inner {
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  border-radius: 8px;
  font-size: 13px; font-weight: 500; color: #121826;
  transition: background .1s, color .1s;
  position: relative; z-index: 1;
}

/* hover */
.rdp-cell:not(.rdp-cell--out):not(.rdp-cell--disabled):hover .rdp-cell-inner {
  background: #f1edf5;
  color: #6B1F4D;
}

/* out-of-month */
.rdp-cell--out .rdp-cell-inner { color: #d8d0e2; cursor: default; }
.rdp-cell--disabled .rdp-cell-inner { color: #d8d0e2; cursor: not-allowed; }

/* today dot */
.rdp-cell--today .rdp-cell-inner::after {
  content: '';
  position: absolute; bottom: 3px; left: 50%;
  transform: translateX(-50%);
  width: 4px; height: 4px;
  background: #6B1F4D; border-radius: 50%;
}

/* in-range band */
.rdp-cell--in::before {
  content: ''; position: absolute;
  top: 50%; transform: translateY(-50%);
  left: 0; right: 0; height: 32px;
  background: #f1edf5;
}
.rdp-cell--in .rdp-cell-inner { color: #6B1F4D; background: transparent; }

/* start */
.rdp-cell--start::before {
  content: ''; position: absolute;
  top: 50%; transform: translateY(-50%);
  left: 50%; right: 0; height: 32px;
  background: #f1edf5;
}
.rdp-cell--start .rdp-cell-inner { background: #6B1F4D !important; color: #fff !important; font-weight: 700; }

/* end */
.rdp-cell--end::before {
  content: ''; position: absolute;
  top: 50%; transform: translateY(-50%);
  left: 0; right: 50%; height: 32px;
  background: #f1edf5;
}
.rdp-cell--end .rdp-cell-inner { background: #6B1F4D !important; color: #fff !important; font-weight: 700; }

/* single day (start = end) */
.rdp-cell--single::before { display: none; }
.rdp-cell--single .rdp-cell-inner { background: #6B1F4D !important; color: #fff !important; font-weight: 700; }

.rdp-selecting-hint {
  margin: 10px 0 0; text-align: center;
  font-size: 11px; color: #b0a0bc;
}

/* ── Side panel ──────────────────────────────────────────────────────── */
.rdp-panel {
  flex: 1;
  padding: 20px 18px 16px;
  display: flex;
  flex-direction: column;
  min-width: 210px;
}

.rdp-panel-title {
  font-size: 10px; font-weight: 800;
  color: #b0a0bc; text-transform: uppercase;
  letter-spacing: .6px; margin: 0 0 8px 4px;
}

.rdp-presets {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 1px;
}
.rdp-preset {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 10px; border-radius: 8px;
  font-size: 13px; color: #374151;
  cursor: pointer;
  transition: background .1s, color .1s;
}
.rdp-preset .rdp-preset-check { font-size: 14px; color: transparent; flex-shrink: 0; }
.rdp-preset:hover { background: #f7f3fb; color: #6B1F4D; }
.rdp-preset--active { background: #f1edf5; color: #6B1F4D; font-weight: 700; }
.rdp-preset--active .rdp-preset-check { color: #6B1F4D; }

.rdp-sep { border: none; border-top: 1px solid #f1edf5; margin: 12px 0; }

/* comparison */
.rdp-compare-header {
  display: flex; align-items: center; gap: 8px;
  cursor: pointer; margin-bottom: 8px; user-select: none;
}
.rdp-check-box .material-icons { font-size: 20px; color: #c8b8d5; transition: color .12s; }
.rdp-check-box--on .material-icons { color: #6B1F4D; }
.rdp-compare-label { font-size: 13px; font-weight: 700; color: #121826; }

.rdp-compare-opts {
  list-style: none; margin: 0; padding: 0;
  display: flex; flex-direction: column; gap: 1px;
}
.rdp-compare-opt {
  display: flex; align-items: center; gap: 8px;
  padding: 7px 10px; border-radius: 8px;
  font-size: 13px; color: #374151;
  cursor: pointer;
  transition: background .1s, color .1s;
}
.rdp-compare-opt:hover:not(.rdp-compare-opt--off) { background: #f7f3fb; color: #6B1F4D; }
.rdp-compare-opt--active { color: #6B1F4D; font-weight: 600; }
.rdp-compare-opt--off { opacity: .38; cursor: not-allowed; }
.rdp-radio {
  width: 7px; height: 7px; border-radius: 50%;
  background: #d1c4da; flex-shrink: 0;
  transition: background .1s;
}
.rdp-compare-opt--active .rdp-radio { background: #6B1F4D; }
.rdp-opt-check { margin-left: auto; font-size: 14px; color: #6B1F4D; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.rdp-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid #f1edf5;
  background: #faf8fc;
}
.rdp-footer-range {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: #6B1F4D;
}
.rdp-footer-range .material-icons { font-size: 14px; }
.rdp-footer-days { font-weight: 400; color: #b0a0bc; }
.rdp-btn-apply {
  background: #6B1F4D; color: #fff;
  border: none; border-radius: 9px;
  padding: 8px 24px;
  font-size: 13px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  transition: opacity .15s, box-shadow .15s;
}
.rdp-btn-apply:hover {
  opacity: .88;
  box-shadow: 0 4px 14px rgba(107,31,77,.25);
}
</style>

<!-- non-scoped : cible la div wrapper du composant Tooltip -->
<style>
.rdp-tt { display: inline-flex !important; }
</style>
